import {
  animate,
  AnimationEvent,
  group,
  keyframes,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { BehaviorSubject, Observable, Subject, interval, EMPTY } from 'rxjs';
import { filter, first, skip, takeUntil, switchMap, scan, takeWhile } from 'rxjs/operators';

import { ComponentPortal, PortalHostDirective, TemplatePortal } from '../../core/core.module';
import { NotificationData } from '../notification-data';
import { NotificationHostBase } from '../notification-host-base';
import { NotificationRef } from '../notification-ref';
import { NotificationStyle } from './../notification-config';

@Component({
  selector: 'rt-notification-host',
  templateUrl: './notification-host.component.html',
  styleUrls: ['./notification-host.component.less'],
  host: {
    '(click)': 'hostClick.emit($event)',
    '(mouseenter)': 'pauseTimer()',
    '(mouseleave)': 'resumeTimer()',
  },
  animations: [
    trigger('toggle', [
      state(
        'created',
        style({ height: 0, opacity: 0, transform: 'translateX(0)', overflow: 'hidden' }),
      ),
      state(
        'in',
        style({ height: '*', opacity: 1, transform: 'translateX(0)', overflow: 'hidden' }),
      ),
      state(
        'out',
        style({
          height: 0,
          opacity: 0,
          transform: 'translateX(0)',
          overflow: 'hidden',
          display: 'none',
        }),
      ),
      transition('* => in', [
        group([
          animate(
            '0.1s ease-out',
            keyframes([
              style({ height: 0, /* TODO NG 8 // opacity: 0, */ offset: 0 }),
              style({ height: '*', /* TODO NG 8 // opacity: 0, */ offset: 1 }),
            ]),
          ),
          animate(
            '0.43s 0.1s ease',
            keyframes([
              style({ opacity: 0, transform: 'translateX(0)', offset: 0 }),
              style({ opacity: 0.5, offset: 0.5 }),
              style({ opacity: 1, transform: 'translateX(0)', offset: 1.0 }),
            ]),
          ),
        ]),
      ]),
      transition('* => out', [
        group([
          animate(
            '0.3s ease',
            keyframes([
              style({ opacity: 1, transform: 'translateX(0)', offset: 0 }),
              style({ opacity: 0.5, offset: 0.5 }),
              style({ opacity: 0, transform: 'translateX(0)', offset: 1 }),
            ]),
          ),
          animate(
            '0.1s 0.3s ease-in',
            keyframes([
              style({ height: '*', /* TODO NG 8 // opacity: 0, */ offset: 0 }),
              style({ height: 0, /* TODO NG 8 // opacity: 0, */ offset: 1 }),
            ]),
          ),
        ]),
      ]),
    ]),
  ],
})
export class NotificationHostComponent extends NotificationHostBase implements OnDestroy {
  @ViewChild(PortalHostDirective, { static: true }) public _portalHost: PortalHostDirective;

  @Output() public topButtonClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() public hostClick: EventEmitter<MouseEvent> = new EventEmitter();

  public timeout: number;
  public NotificationStyle: typeof NotificationStyle = NotificationStyle;

  public className: string;
  public state: 'created' | 'in' | 'out' | 'done' = 'created';
  public percentsOfProgress: number = 0;
  public currentNotificationStyle: NotificationStyle;

  private ngOnDestroy$: Subject<void> = new Subject();
  private animationState$: Subject<AnimationEvent> = new Subject();
  private style$: BehaviorSubject<string>;
  private pause$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    public viewContainerRef: ViewContainerRef,
    @Optional() public notificationRef: NotificationRef,
    @Optional() public data: NotificationData,
    private ngZone: NgZone,
    private cdRef: ChangeDetectorRef,
  ) {
    super();

    const currentNotificationStyle = notificationRef
      ? notificationRef!.config!.style!
      : NotificationStyle.Default;

    this.style$ = new BehaviorSubject(this.getClass(currentNotificationStyle));
    this.currentNotificationStyle = currentNotificationStyle;
    this.timeout = notificationRef ? notificationRef!.config!.timeout! - 1000 : 5000;

    this.style$
      .asObservable()
      .pipe(first())
      .subscribe(className => {
        this.className = className + ' notification-container';
      });
  }

  public ngOnInit(): void {
    this.animationState$
      .asObservable()
      .pipe(
        filter(
          event =>
            event.fromState === 'void' && event.toState === 'created' && event.phaseName === 'done',
        ),
        first(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.state = 'in';
        this.cdRef.detectChanges();
      });

    this.style$
      .asObservable()
      .pipe(skip(1), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(className => {
        this.ngZone.run(() => {
          this.className = className + ' notification-container';
          this.cdRef.detectChanges();
        });
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngAfterViewInit(): void {
    this.currentNotificationStyle === NotificationStyle.Timeout && this.startTimer();
  }

  public attachTemplatePortal(portal: TemplatePortal): Map<string, any> {
    return this._portalHost.attachTemplatePortal(portal);
  }

  public attachComponentPortal<T>(portal: ComponentPortal<T>, index?: number): ComponentRef<T> {
    return this._portalHost.attachComponentPortal(portal, index);
  }

  public close(): void {
    this.state = 'out';
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
  }

  public get beforeClose(): Observable<AnimationEvent> {
    return this.animationState$.asObservable().pipe(
      filter(event => event.toState === 'out' && event.phaseName === 'start'),
      first(),
    );
  }

  public get afterClosed(): Observable<AnimationEvent> {
    return this.animationState$.asObservable().pipe(
      filter(event => event && event.toState === 'out' && event.phaseName === 'done'),
      first(),
    );
  }

  public dismiss($event: MouseEvent): void {
    if (this.notificationRef) {
      $event.preventDefault();
      $event.stopPropagation();

      this.notificationRef.close();
    }
  }

  public setStyle(notificationStyle: NotificationStyle): void {
    this.style$.next(this.getClass(notificationStyle));
  }

  public hide(): Observable<AnimationEvent> {
    this.state = 'out';
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
    return this.afterClosed;
  }

  public onAnimationEvent($event: AnimationEvent): void {
    this.animationState$.next($event);
  }

  public pauseTimer(): void {
    this.currentNotificationStyle === NotificationStyle.Timeout && this.pause$.next(true);
  }

  public resumeTimer(): void {
    this.currentNotificationStyle === NotificationStyle.Timeout && this.pause$.next(false);
  }

  private startTimer(): void {
    const timeInterval = 100;
    const interval$ = interval(timeInterval);

    this.pause$
      .pipe(
        switchMap(paused => (paused ? EMPTY : interval$)),
        scan(acc => acc + timeInterval, 0),
        takeWhile(counter => counter <= this.timeout),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(counter => {
        const percentsOfProgress = (counter / this.timeout) * 100;
        this.percentsOfProgress = percentsOfProgress;
        this.cdRef.detectChanges();
      });
  }

  private getClass(
    notificationStyle: NotificationStyle,
  ): 'success' | 'wait' | 'error' | 'warning' | 'in-progress' | 'timeout' | '' {
    switch (notificationStyle) {
      case NotificationStyle.Success:
        return 'success';

      case NotificationStyle.Wait:
        return 'wait';

      case NotificationStyle.Error:
        return 'error';

      case NotificationStyle.Warning:
        return 'warning';

      case NotificationStyle.InProgress:
        return 'in-progress';

      case NotificationStyle.Timeout:
        return 'timeout';

      default:
        return '';
    }
  }
}
